import { takeLatest, call, put, all, select } from 'redux-saga/effects'
import { initialize, change } from 'redux-form'

import { pushLocation } from '../../../store/location'

import { CONSTANTS } from './reducer'
import { showNotification } from '../../../store/global'
import {
  getDetailEmployee,
  getHouseMaster,
  updateHouseMaster,
  updateEmployeeProfile,
  uploadAvatar
} from '../../../api/employee'
import { leaveRequestStaticSummary } from '../../../api/leaveRequest'
import { getCountry, getProvince, getDistrict } from '../../../api/general'
import { getUserData } from '../../../utils/token'
import { changeAvatar } from '../../../store/authentication'

function* onGetProfile({ employeeId }) {
  try {
    const user = getUserData()
    const [profileResponse, houseMaster] = yield all([
      call(getDetailEmployee, employeeId, 'profile'),
      // call(leaveRequestStaticSummary, { employeeId }),
      call(getHouseMaster, employeeId)
    ])
    let summaryResponse
    if (employeeId === user.EmployeeId) {
      summaryResponse = yield call(leaveRequestStaticSummary, {
        employeeId: profileResponse.Data.StaffId
      })
    }
    if (profileResponse.HasError) {
      throw new Error(profileResponse.Error)
    }

    if (summaryResponse && summaryResponse.HasError) {
      throw new Error(summaryResponse.Error)
    }

    if (houseMaster.HasError) {
      throw new Error(houseMaster.Error)
    }
    yield put({
      type: CONSTANTS.PROFILE_BASIC_SUCCESS,
      profile: profileResponse.Data,
      leaveTypes: summaryResponse ? summaryResponse.Data.ListData : [],
      annualLeave: summaryResponse
        ? summaryResponse.Data.AnnualLeaveSummary
        : {},
      houseMaster: houseMaster.Data,
      canEdit: employeeId == user.EmployeeId
    })
  } catch (error) {
    const message =
      (error.response && error.response.data.Message) || error.message
    yield put(showNotification(message, 30000, true))
    yield put({ type: CONSTANTS.PROFILE_BASIC_FAILURE })
  }
}

function* onGetDetail({ employeeId }) {
  try {
    const [userResponse, houseHolderResponse] = yield all([
      call(getDetailEmployee, employeeId, 'detail'),
      call(getHouseMaster, employeeId)
    ])

    if (userResponse.HasError) {
      throw new Error(userResponse.Error)
    }

    if (houseHolderResponse.HasError) {
      throw new Error(houseHolderResponse.Error)
    }

    yield put({
      type: CONSTANTS.PROFILE_DETAIL_SUCCESS,
      detail: userResponse.Data,
      houseMaster: houseHolderResponse.Data
    })

    yield* onGetCountries()
    if (userResponse.Data.ResidentialCountryId) {
      yield* onGetProvinces({
        countryID: userResponse.Data.ResidentialCountryId
      })
    }
    if (userResponse.Data.MailingCountryId) {
      yield* onGetProvinces({ countryID: userResponse.Data.MailingCountryId })
    }
    if (userResponse.Data.ResidentialProvinceId) {
      yield* onGetDistricts({
        provinceID: userResponse.Data.ResidentialProvinceId
      })
    }
    if (userResponse.Data.MailingProvinceId) {
      yield* onGetDistricts({ provinceID: userResponse.Data.MailingProvinceId })
    }

    yield put(
      initialize('EditProfile', {
        residentialCountry: userResponse.Data.ResidentialCountryId,
        residentialProvince: userResponse.Data.ResidentialProvinceId,
        residentialDistrict: userResponse.Data.ResidentialDistrictId,
        residentialAddress: userResponse.Data.ResidentialAddress,
        mailingCountry: userResponse.Data.MailingCountryId,
        mailingProvince: userResponse.Data.MailingProvinceId,
        mailingDistrict: userResponse.Data.MailingDistrictId,
        mailingAddress: userResponse.Data.MailingAddress,
        homePhone: userResponse.Data.HomePhoneNo,
        cellPhone: userResponse.Data.CellPhoneNo,
        workPhone: userResponse.Data.WorkPhoneNo,
        workPhoneExt: userResponse.Data.WorkPhoneExt,
        workEmail: userResponse.Data.Email,
        personalEmail: userResponse.Data.PersonalEmail,
        skypeID: userResponse.Data.SkypeId,
        maritalStatus: userResponse.Data.MaritalStatus,
        hobbies: userResponse.Data.Hobbies
      })
    )
  } catch (error) {
    const message =
      (error.response && error.response.data.Message) || error.message
    yield put(showNotification(message, 30000, true))
    yield put({ type: CONSTANTS.PROFILE_DETAIL_FAILURE })
  }
}

function* onGetCountries() {
  try {
    const countries = yield select(state => state.profile.countries)

    if (countries.length > 0) {
      return
    }

    const [firstGroup, secondGroup] = yield all([
      call(getCountry, 1, 200),
      call(getCountry, 2, 200)
    ])

    if (firstGroup.HasError) {
      throw new Error(firstGroup.Error)
    }

    if (secondGroup.HasError) {
      throw new Error(secondGroup.Error)
    }

    yield put(change('EditProfile', 'residentialDistrict', null))
    yield put(change('EditProfile', 'residentialProvince', null))

    yield put({
      type: CONSTANTS.COUNTRY_SUCCESS,
      countries: [...firstGroup.Data.DataList, ...secondGroup.Data.DataList]
    })
  } catch (error) {
    const message =
      (error.response && error.response.data.Message) || error.message
    yield put(showNotification(message, 30000, true))
    yield put({ type: CONSTANTS.COUNTRY_FAILURE })
  }
}

function* onGetProvinces({ countryID }) {
  try {
    const provinces = yield select(state => state.profile.provinces[countryID])

    if (provinces) {
      return
    }

    const response = yield call(getProvince, countryID, 1, 230)

    if (response.HasError) {
      throw new Error(response.Error)
    }

    yield put(change('EditProfile', 'residentialProvince', null))

    yield put({
      type: CONSTANTS.PROVINCE_SUCCESS,
      provinces: response.Data.DataList,
      countryID
    })
  } catch (error) {
    const message =
      (error.response && error.response.data.Message) || error.message
    yield put(showNotification(message, 30000, true))
    yield put({ type: CONSTANTS.PROVINCE_FAILURE })
  }
}

function* onGetDistricts({ provinceID }) {
  try {
    const districts = yield select(state => state.profile.districts[provinceID])

    if (districts) {
      return
    }

    const response = yield call(getDistrict, provinceID, 1, 200)

    if (response.HasError) {
      throw new Error(response.Error)
    }

    yield put(change('EditProfile', 'residentialDistrict', null))

    yield put({
      type: CONSTANTS.DISTRICT_SUCCESS,
      districts: response.Data.DataList,
      provinceID
    })
  } catch (error) {
    const message =
      (error.response && error.response.data.Message) || error.message
    yield put(showNotification(message, 30000, true))
    yield put({ type: CONSTANTS.DISTRICT_FAILURE })
  }
}

function* onSaveProfile({ employeeId, profile }) {
  try {
    console.log(employeeId)
    console.log(profile)
    const params = {
      ResidentialAddress: profile.residentialAddress,
      ResidentialWardId: '',
      ResidentialDistrictId: profile.residentialDistrict,
      ResidentialProvinceId: profile.residentialProvince,
      ResidentialCountryId: profile.residentialCountry,
      MailingAddress: profile.mailingAddress,
      MailingWardId: '',
      MailingDistrictId: profile.mailingDistrict,
      MailingProvinceId: profile.mailingProvince,
      MailingCountryId: profile.mailingCountry,
      HomePhoneNo: profile.homePhone,
      CellPhoneNo: profile.cellPhone,
      WorkPhoneNo: profile.workPhone,
      WorkPhoneExt: profile.workPhoneExt,
      PersonalEmail: profile.personalEmail,
      YahooId: '',
      SkypeId: profile.skypeID,
      MaritalStatus: profile.maritalStatus,
      Hobbies: profile.hobbies,
      Avatar: profile.Avatar,
      Photo: profile.Photo
    }
    const { houseMaster, countries, provinces, districts } = yield select(
      state => state.profile
    )

    if (houseMaster) {
      const country = countries.find(
        country => country.Id === profile.residentialCountry
      )
      const province = provinces[profile.residentialCountry].find(
        province => province.Id === profile.residentialProvince
      )
      const district = districts[profile.residentialProvince].find(
        district => district.Id === profile.residentialDistrict
      )
      const houseMasterParams = {
        ...houseMaster,
        Address: [
          profile.residentialAddress,
          district.DistrictName,
          province.ProvinceName,
          country.CountryName
        ]
          .join(', ')
          .trim()
      }

      const houseHolderResponse = yield call(
        updateHouseMaster,
        houseMasterParams
      )

      if (houseHolderResponse.HasError) {
        throw new Error(houseHolderResponse.Error)
      }
    }
    const userResponse = yield call(updateEmployeeProfile, employeeId, params)

    if (userResponse.HasError) {
      throw new Error(userResponse.Error)
    }

    yield put({
      type: CONSTANTS.SAVE_PROFILE_DETAIL_SUCCESS
    })

    yield put(showNotification('Successful'))

    yield* onGetDetail({ employeeId })

    yield put(pushLocation(`/profile/${employeeId}`))
  } catch (error) {
    const message =
      (error.response && error.response.data.Message) || error.message
    yield put(showNotification(message, 30000, true))
    yield put({ type: CONSTANTS.SAVE_PROFILE_DETAIL_FAILURE })
  }
}

function* onUploadAvatar({ blob }) {
  try {
    if (!blob) {
      throw new Error('Invalid image')
    }

    const user = getUserData()

    const data = new FormData()
    data.append('ID', user.EmployeeId)
    data.append('file', blob)

    const response = yield call(uploadAvatar, data)

    if (response.HasError) {
      throw new Error(response.Error)
    }

    yield put({ type: CONSTANTS.UPLOAD_AVATAR_SUCCESS })
    yield put(changeAvatar(response.Data.image))
  } catch (error) {
    const message =
      (error.response && error.response.data.Message) || error.message
    yield put(showNotification(message, 30000, true))
    yield put({ type: CONSTANTS.UPLOAD_AVATAR_FAILURE })
  }
}

function* profileWatcher() {
  yield all([
    takeLatest(CONSTANTS.PROFILE_BASIC_REQUEST, onGetProfile),
    takeLatest(CONSTANTS.PROFILE_DETAIL_REQUEST, onGetDetail),
    takeLatest(CONSTANTS.COUNTRY_REQUEST, onGetCountries),
    takeLatest(CONSTANTS.PROVINCE_REQUEST, onGetProvinces),
    takeLatest(CONSTANTS.DISTRICT_REQUEST, onGetDistricts),
    takeLatest(CONSTANTS.SAVE_PROFILE_DETAIL_REQUEST, onSaveProfile),
    takeLatest(CONSTANTS.UPLOAD_AVATAR_REQUEST, onUploadAvatar)
  ])
}

export default [profileWatcher()]



// WEBPACK FOOTER //
// ./src/routes/UserProfile/modules/saga.js